import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import type { FormSubmissionResult } from '../../../../types/publicForm.types';

interface V1FormConfirmationProps {
  form: any;
  confirmationData: FormSubmissionResult;
  primaryColor: string;
  onNewSubmission: () => void;
  onEditSubmission: () => void;
}

/**
 * V1 Form Confirmation Component - FROZEN
 *
 * Displays confirmation after successful V1 form submission.
 * Does NOT include V2 features like:
 * - PDF downloads
 * - QR codes
 * - Complex approval workflows
 *
 * V1 forms show:
 * - Success message
 * - Confirmation number
 * - Submit another response button
 * - Back to event details button
 */
export const V1FormConfirmation: React.FC<V1FormConfirmationProps> = ({
  form,
  confirmationData,
  primaryColor,
  onNewSubmission,
  onEditSubmission,
}) => {
  const navigate = useNavigate();
  const confirmationMessage = confirmationData.confirmationMessage ||
    form.confirmationEmailBody ||
    'Thank you for your submission.';

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div
              className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Check className="h-8 w-8" style={{ color: primaryColor }} />
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Form Submitted Successfully!
            </h2>

            {confirmationData.confirmationNumber && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1">Confirmation Number</p>
                <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                  {confirmationData.confirmationNumber}
                </p>
              </div>
            )}

            <div
              className="prose prose-sm max-w-none text-gray-700 mx-auto mb-8"
              dangerouslySetInnerHTML={{ __html: confirmationMessage }}
            />

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={onNewSubmission}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-md font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Submit Another Response
              </button>

              {confirmationData.editUrl && form.editSubmissions && (
                <button
                  onClick={onEditSubmission}
                  className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Edit Submission
                </button>
              )}

              <button
                onClick={() => {
                  // Navigate back to forms list using React Router
                  navigate('/forms');
                }}
                className="px-6 py-3 text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  backgroundColor: primaryColor,
                }}
              >
                Back to Forms
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};